import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { toBandSeries, humanEUR } from "./format";
import { singleSeriesMeta, endLabel } from "./chartAnnotations";
import { forecastLabUrl } from "../forecast-lab/deepLink";

const HORIZONS = [30, 60, 90];

const ACTUAL_COLOR = "hsl(var(--chart-1))";
const FORECAST_COLOR = "hsl(var(--chart-1))";
// Neutral grey-blue for the range, matching the cost-analyzer: the band is context for
// the forecast line, not a second series competing with it for the accent hue.
const BAND_COLOR = "hsl(var(--chart-axis))";
const BAND_FILL_OPACITY = 0.08;
const BUDGET_COLOR = "hsl(var(--danger))";
const GRID_COLOR = "hsl(var(--chart-grid))";
const AXIS_COLOR = "hsl(var(--chart-axis))";
const REF_COLOR = "hsl(var(--chart-axis))";

// eurFull formats a whole-euro value, e.g. "€24,000". Tooltips show a precise value
// (the compact "€24k" form lives on the KPI tiles).
function eurFull(v) {
  const n = Math.round(Number(v) || 0);
  return `€${n.toLocaleString("en-US")}`;
}

// fmtDate normalises a date to dayjs and formats it. The backend sends ISO dates,
// but tolerate a legacy YYYYMMDD id too so a lagging backend can't break the axis.
function fmtDate(v, pattern) {
  if (!v) return "";
  const s = /^\d{8}$/.test(v) ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : v;
  const d = dayjs(s);
  return d.isValid() ? d.format(pattern) : String(v);
}

// budgetValue returns the (flat) daily budget line from the first point that has one.
function budgetValue(series) {
  const p = (series || []).find((d) => d.budget != null);
  return p ? p.budget : null;
}

// LegendSwatch renders the small marker used in the chart legend as inline SVG so
// it always paints crisply (a zero-height bordered span collapses inconsistently).
function LegendSwatch({ kind, color }) {
  if (kind === "area") {
    return (
      <svg width="18" height="10" aria-hidden="true" className="shrink-0">
        <rect x="0" y="1" width="18" height="8" rx="2" fill={color} fillOpacity="0.5" />
      </svg>
    );
  }
  return (
    <svg width="18" height="10" aria-hidden="true" className="shrink-0">
      <line
        x1="0"
        y1="5"
        x2="18"
        y2="5"
        stroke={color}
        strokeWidth="2.5"
        strokeDasharray={kind === "dashed" ? "5 3" : undefined}
        strokeLinecap="round"
      />
    </svg>
  );
}
LegendSwatch.propTypes = { kind: PropTypes.string, color: PropTypes.string };

// ChartLegend mirrors the cost-analyzer legend: small markers + muted labels, inline at
// the top-right of the card so the plot area keeps its full height and the key sits
// beside the title rather than stranded under the axis.
function ChartLegend({ hasBand, hasBudget, hasForecast, intervalPct }) {
  const items = [{ key: "actual", label: "Actual", kind: "solid", color: ACTUAL_COLOR }];
  if (hasForecast) items.push({ key: "forecast", label: "Forecast", kind: "dashed", color: FORECAST_COLOR });
  if (hasBand) items.push({ key: "band", label: `${intervalPct}% confidence`, kind: "area", color: BAND_COLOR });
  if (hasBudget) items.push({ key: "budget", label: "Budget", kind: "dashed", color: BUDGET_COLOR });
  return (
    <ul className="flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-xs text-text-2">
      {items.map((it) => (
        <li key={it.key} className="flex items-center gap-1.5">
          <LegendSwatch kind={it.kind} color={it.color} />
          <span>{it.label}</span>
        </li>
      ))}
    </ul>
  );
}
ChartLegend.propTypes = {
  hasBand: PropTypes.bool,
  hasBudget: PropTypes.bool,
  hasForecast: PropTypes.bool,
  intervalPct: PropTypes.number,
};

// ForecastTooltip mirrors the cost-analyzer tooltip: a "dddd, MMMM D" header, then
// the relevant value(s) for the hovered day in a large, legible figure.
function ForecastTooltip({ active, payload, intervalPct = 80 }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload || {};
  const isForecast = p.forecast != null;
  const primaryLabel = isForecast ? "Forecast" : "Actual";
  const primaryVal = isForecast ? p.forecast : p.actual;
  if (primaryVal == null && p.budget == null) return null;
  return (
    <div className="rounded-card border border-card-bd bg-card px-3.5 py-3">
      <div className="mb-1.5 text-sm font-semibold text-text-2">
        {fmtDate(p.date, "dddd, MMMM D")}
      </div>
      {primaryVal != null && (
        <div className="mb-1">
          <div className="text-xs text-text-2">{primaryLabel}</div>
          <div className="text-xl font-semibold tabular-nums text-text">{eurFull(primaryVal)}</div>
        </div>
      )}
      {p.lower != null && p.upper != null && (
        <div className="mb-1 text-xs text-text-2">
          {intervalPct}% range <span className="tabular-nums text-text">{eurFull(p.lower)} – {eurFull(p.upper)}</span>
        </div>
      )}
      {p.budget != null && (
        <div className="text-xs text-text-2">
          Budget (daily) <span className="tabular-nums text-text">{eurFull(p.budget)}</span>
        </div>
      )}
    </div>
  );
}
ForecastTooltip.propTypes = { active: PropTypes.bool, payload: PropTypes.array, intervalPct: PropTypes.number };

// ForecastChart shows actual + forecast lines, a confidence band (intervalPct,
// default 80%), and a daily budget reference line, with a 30/60/90-day horizon
// toggle.
//
// It carries the same evidence-chart language as the cost-analyzer's daily-spend chart —
// unsmoothed lines, a run-rate average, the "Forecast starts" boundary, a peak callout and
// end-of-line labels — since both answer the same question and analysts read them back to
// back. The shared derivation lives in chartAnnotations.
const ForecastChart = ({ series = [], horizon = 90, onHorizonChange, intervalPct = 80, showLabLink = false, baseline = null }) => {
  // Defensive ascending sort by date (the backend sorts, but never trust order).
  const sorted = useMemo(
    () => [...(series || [])].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [series]
  );
  const data = useMemo(() => toBandSeries(sorted), [sorted]);
  const budget = budgetValue(sorted);
  const hasBand = data.some((d) => Array.isArray(d.band));
  // Same derivation the cost-analyzer's daily-spend chart uses, so the two charts place
  // their run-rate average, settle boundary, peak and end labels identically.
  const meta = useMemo(() => singleSeriesMeta(data), [data]);
  // Prefer an explicit baseline from the caller; otherwise annotate the actual run-rate.
  const avg = Number.isFinite(baseline) ? baseline : meta.avg;
  const peakVsAvg = meta.peak && meta.avg > 0
    ? Math.round((100 * (meta.peak.actual - meta.avg)) / meta.avg)
    : null;

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="text-xs uppercase tracking-wide text-text-2">Forecast vs budget</div>
        <div className="flex items-center gap-4">
          <ChartLegend
            hasBand={hasBand}
            hasBudget={budget != null}
            hasForecast={meta.hasForecast}
            intervalPct={intervalPct}
          />
          {/* Only show the horizon toggle when it's actually wired — otherwise it's
              a dead control. */}
          {onHorizonChange && (
            <div className="flex shrink-0 gap-1" role="group" aria-label="Forecast horizon">
              {HORIZONS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => onHorizonChange(h)}
                  className={`rounded-full px-3 py-0.5 text-xs ${
                    h === horizon ? "bg-primary text-white" : "bg-surface-2 text-text-2"
                  }`}
                >
                  {h}d
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Date-range subtitle in the cost-analyzer's wording, so the two charts state their
          window the same way. The unit is carried by the "Avg €N/day" reference line and the
          y-axis; what's left to say is how the chart relates to the Projected tile. The Lab
          link carries the current horizon so the Explorer opens on the same window. */}
      <div className="mt-0.5 flex items-center justify-between gap-3">
        <div className="text-[11px] text-text-3">
          {meta.firstDate && (
            <>
              {fmtDate(meta.firstDate, "MMM D")} – {fmtDate(meta.lastDate, "MMM D")}
              {meta.boundary && ` · actuals through ${fmtDate(meta.boundary, "MMM D")}`}
              {meta.hasForecast && ` · forecast to ${fmtDate(meta.lastDate, "MMM D")}`}
              {" · "}
            </>
          )}
          Projected tile = {horizon}-day total
        </div>
        {/* Only offer the Lab link from outside the Lab (e.g. the dashboard). */}
        {showLabLink && (
          <Link
            to={forecastLabUrl({ tab: "explorer", lens: "cost-series", horizon })}
            className="shrink-0 whitespace-nowrap text-xs font-medium text-primary hover:text-primary-hover hover:underline"
          >
            Explore in Forecast Lab →
          </Link>
        )}
      </div>
      {/* Height and margins match the analyzer's single-series chart; the right margin
          leaves room for the end-of-line "Actual"/"Forecast" labels. */}
      <div className="mt-3" style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 22, right: 64, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: AXIS_COLOR }}
              tickLine={false}
              axisLine={{ stroke: GRID_COLOR }}
              minTickGap={24}
              tickMargin={8}
              tickFormatter={(v) => fmtDate(v, "MMM D")}
            />
            <YAxis
              tick={{ fontSize: 11, fill: AXIS_COLOR }}
              tickLine={false}
              axisLine={false}
              width={64}
              domain={[0, "auto"]}
              allowDataOverflow
              tickFormatter={(v) => humanEUR(v)}
            />
            <Tooltip
              content={<ForecastTooltip intervalPct={intervalPct} />}
              cursor={{ stroke: AXIS_COLOR, strokeWidth: 1, strokeDasharray: "3 3" }}
            />
            {/* confidence band — rendered first so lines paint on top */}
            <Area type="linear" dataKey="band" stroke="none" fill={BAND_COLOR} fillOpacity={BAND_FILL_OPACITY} isAnimationActive={false} connectNulls={false} />
            {budget != null && (
              <ReferenceLine
                y={budget}
                stroke={BUDGET_COLOR}
                strokeDasharray="4 4"
                label={{ value: "Budget", position: "insideTopRight", fontSize: 11, fill: BUDGET_COLOR, dy: -2 }}
              />
            )}
            {/* Run-rate reference: "is the forecast above or below how we've been spending?" */}
            {Number.isFinite(avg) && (
              <ReferenceLine
                y={avg}
                stroke={REF_COLOR}
                strokeDasharray="4 4"
                strokeOpacity={0.7}
                ifOverflow="extendDomain"
                label={{ value: `Avg ${humanEUR(avg)}/day`, position: "insideTopLeft", fill: AXIS_COLOR, fontSize: 11 }}
              />
            )}
            {/* Where measurement stops and projection begins. */}
            {meta.hasForecast && meta.boundary && (
              <ReferenceLine
                x={meta.boundary}
                stroke={REF_COLOR}
                strokeDasharray="3 3"
                strokeOpacity={0.8}
                label={{ value: `Forecast starts · ${fmtDate(meta.boundary, "MMM D")}`, position: "insideBottom", fill: AXIS_COLOR, fontSize: 11 }}
              />
            )}
            {/* No connectNulls: actual and forecast must NOT bridge across the
                today boundary, where each series is null on the other's side. */}
            <Line
              type="linear"
              dataKey="actual"
              stroke={ACTUAL_COLOR}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
              label={endLabel("Actual", meta.lastActualIdx, ACTUAL_COLOR)}
            />
            <Line
              type="linear"
              dataKey="forecast"
              stroke={FORECAST_COLOR}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
              label={endLabel("Forecast", meta.lastForecastIdx, FORECAST_COLOR)}
            />
            {/* Peak annotation — the spike worth explaining. */}
            {meta.peak && (
              <ReferenceDot
                x={meta.peak.date}
                y={meta.peak.actual}
                r={3.5}
                fill={ACTUAL_COLOR}
                stroke="hsl(var(--card))"
                strokeWidth={1.5}
                label={{
                  value: peakVsAvg != null ? `Peak ${humanEUR(meta.peak.actual)} (+${peakVsAvg}% vs avg)` : `Peak ${humanEUR(meta.peak.actual)}`,
                  position: "top",
                  fill: ACTUAL_COLOR,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

ForecastChart.propTypes = {
  series: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      actual: PropTypes.number,
      forecast: PropTypes.number,
      lower: PropTypes.number,
      upper: PropTypes.number,
      budget: PropTypes.number,
    })
  ),
  horizon: PropTypes.number,
  onHorizonChange: PropTypes.func,
  intervalPct: PropTypes.number,
  showLabLink: PropTypes.bool,
  baseline: PropTypes.number,
};

export default ForecastChart;
